<script setup lang="ts">
/**
 * Footer V2 — Coluna de menu (título + lista de links).
 *
 * Renderiza <NuxtLink> para rotas internas e <a target=_blank rel="noopener noreferrer">
 * para URLs externas (segurança OWASP — evita window.opener tampering).
 */
import type { FooterMenuGroup } from '@/composables/useFooterLinks';

defineProps<{
  group: FooterMenuGroup;
}>();
</script>

<template>
  <nav
    :aria-label="group.title"
    class="footer-v2-column"
  >
    <h3 class="footer-v2-column__title">{{ group.title }}</h3>

    <ul class="footer-v2-column__list">
      <li
        v-for="link in group.links"
        :key="link.to"
        class="footer-v2-column__item"
      >
        <a
          v-if="link.external"
          class="footer-v2-column__link"
          :href="link.to"
          rel="noopener noreferrer"
          target="_blank"
        >
          {{ link.i18n ? $t(link.label) : link.label }}
        </a>
        <NuxtLink
          v-else
          class="footer-v2-column__link"
          no-prefetch
          :to="link.to"
        >
          {{ link.i18n ? $t(link.label) : link.label }}
        </NuxtLink>
      </li>
    </ul>
  </nav>
</template>

<style scoped lang="scss">
.footer-v2-column {
  @apply flex flex-col;
  gap: 8px;

  /* Desktop: gap interno entre título e lista é menor (visual mais
     compacto na grid de 4 colunas). Mantém 8px no mobile. */
  @media (min-width: 1024px) {
    gap: 4px;
  }

  &__title {
    color: rgba(255, 255, 255, 0.5);
    font-family: 'Plus Jakarta Sans', sans-serif;
    font-size: 12px;
    font-weight: 600;
    line-height: 16px;
    margin: 0;

    /* Desktop: weight 400 (mais leve que o accordion mobile) */
    @media (min-width: 1024px) {
      font-weight: 400;
    }
  }

  &__list {
    @apply flex flex-col;
    list-style: none;
    padding: 0;
    margin: 0;
    gap: 4px;
  }

  &__link {
    color: rgba(255, 255, 255, 0.5);
    font-family: 'Plus Jakarta Sans', sans-serif;
    font-size: 14px;
    font-weight: 600;
    line-height: 20px;
    text-decoration: none;
    transition: color 0.15s ease;

    /* Desktop: weight 700 (links com mais peso visual). */
    @media (min-width: 1024px) {
      font-weight: 700;
    }

    &:hover,
    &:focus-visible {
      color: rgba(255, 255, 255, 0.85);
    }

    &:focus-visible {
      outline: 2px solid rgba(255, 255, 255, 0.6);
      outline-offset: 2px;
      border-radius: 2px;
    }
  }
}
</style>
